import { Check, ChevronsUpDown, Search, Store } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { cn } from "../lib/cn.js";
import type { SessionStore } from "../lib/session.js";

/**
 * The store picker ?" button trigger with popover search list.
 *
 * Button trigger ensures NO blinking text cursor appears on the store name.
 * Reliable single-click toggle with auto-focused search inside the dropdown.
 */
export function StorePicker({
  stores,
  selected,
  onSelect,
}: {
  readonly stores: readonly SessionStore[];
  readonly selected: SessionStore | null;
  readonly onSelect: (store: SessionStore) => void;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === "") return stores;
    return stores.filter(
      (store) =>
        store.displayName.toLowerCase().includes(needle) || store.shopDomain.toLowerCase().includes(needle),
    );
  }, [stores, query]);

  useEffect(() => {
    setActive((current) => (current >= matches.length ? 0 : current));
  }, [matches.length]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (root.current !== null && !root.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (stores.length === 0) return null;
  if (stores.length === 1) {
    const only = stores[0];
    return (
      <div className="bg-card text-muted-foreground flex h-9 items-center gap-2 rounded-md border px-3 text-sm">
        <Store className="size-4 shrink-0" />
        <span className="max-w-[16rem] truncate">{only?.displayName}</span>
      </div>
    );
  }

  const label = selected === null ? "Select store" : selected.displayName;

  const choose = (store: SessionStore): void => {
    onSelect(store);
    setOpen(false);
    setQuery("");
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <div ref={root} className="relative w-[min(18rem,42vw)]">
      {/* Trigger Button: Clean select button, NO blinking text cursor */}
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label="Store"
        onClick={() => setOpen((prev) => !prev)}
        className="bg-card hover:bg-accent/40 relative flex h-9 w-full cursor-pointer items-center justify-between rounded-md border px-3 text-left shadow-xs transition-colors select-none"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
          <Store className="text-muted-foreground size-4 shrink-0" />
          <span className="truncate text-sm font-medium text-foreground">
            {label}
          </span>
        </div>
        <ChevronsUpDown className="text-muted-foreground size-4 shrink-0" />
      </button>

      {open && (
        <div
          id={listId}
          className="bg-popover absolute inset-x-0 z-50 mt-1 max-h-80 w-full overflow-hidden rounded-md border p-1 shadow-md"
        >
          {/* Search box inside dropdown */}
          <div className="flex items-center border-b px-2 pb-1.5 pt-1">
            <Search className="text-muted-foreground size-3.5 shrink-0 mr-1.5" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search store name or domain..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setActive((current) => (matches.length === 0 ? 0 : (current + 1) % matches.length));
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setActive((current) => (matches.length === 0 ? 0 : (current - 1 + matches.length) % matches.length));
                } else if (event.key === "Enter") {
                  const store = matches[active];
                  if (store !== undefined) {
                    event.preventDefault();
                    choose(store);
                  }
                } else if (event.key === "Escape") {
                  setOpen(false);
                  setQuery("");
                }
              }}
              className="w-full bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
            />
          </div>

          <ul role="listbox" aria-label="Stores" className="max-h-60 overflow-auto pt-1">
            {matches.length === 0 && (
              <li className="text-muted-foreground px-2 py-3 text-xs text-center">No stores match.</li>
            )}
            {matches.map((store, index) => {
              const isSelected = store.tenantId === selected?.tenantId;
              return (
                <li
                  key={store.tenantId}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => choose(store)}
                  onMouseEnter={() => setActive(index)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
                    index === active && "bg-accent text-accent-foreground",
                  )}
                >
                  <Check className={cn("size-4 shrink-0 text-primary", isSelected ? "opacity-100" : "opacity-0")} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{store.displayName}</span>
                    <span className="text-muted-foreground block truncate text-xs">{store.shopDomain}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
